import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, CheckCircle, Building2, Users, TrendingUp, Activity, Mail, Database, Edit, Trash2, Power, PowerOff, AlertTriangle, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LocationManagement = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Edit dialog state
  const [editDialog, setEditDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  
  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deletingLocation, setDeletingLocation] = useState(null);
  const [deleteStats, setDeleteStats] = useState(null);

  useEffect(() => {
    fetchCurrentUser();
    fetchLocations();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Failed to fetch current user');
    }
  };

  const fetchLocations = async () => {
    try {
      // Use the new management endpoint if super admin
      const response = await axios.get(`${API}/locations/management/all`);
      setLocations(response.data);
      
      // Get current location from localStorage
      const savedLocationId = localStorage.getItem('currentLocationId');
      setCurrentLocation(savedLocationId);
    } catch (error) {
      console.error('Error fetching locations:', error);
      if (error.response?.status === 403) {
        toast.error('You need super admin access to manage locations');
        navigate('/switch-location');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLocation = (locationId) => {
    setCurrentLocation(locationId);
    localStorage.setItem('currentLocationId', locationId);
    toast.success('Location switched successfully');
    setTimeout(() => {
      navigate('/');
      window.location.reload();
    }, 500);
  };

  const handleEditLocation = (location) => {
    setEditingLocation({...location});
    setEditDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/locations/${editingLocation.id}`, {
        name: editingLocation.name,
        address: editingLocation.address,
        city: editingLocation.city,
        state: editingLocation.state,
        zip: editingLocation.zip,
        phone: editingLocation.phone,
        email: editingLocation.email,
        primary_contact_name: editingLocation.primary_contact_name,
        primary_contact_email: editingLocation.primary_contact_email
      });
      
      toast.success('Location updated successfully');
      setEditDialog(false);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update location');
    }
  };

  const handleToggleStatus = async (locationId) => {
    try {
      const response = await axios.patch(`${API}/locations/${locationId}/toggle-status`);
      toast.success(response.data.message);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle location status');
    }
  };

  const handleDeleteClick = async (location) => {
    try {
      // Get detailed stats for this location
      const response = await axios.get(`${API}/locations/${location.id}/stats`);
      setDeleteStats(response.data);
      setDeletingLocation(location);
      setDeleteDialog(true);
    } catch (error) {
      toast.error('Failed to load location details');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await axios.delete(`${API}/locations/${deletingLocation.id}`);
      
      if (response.data.success) {
        toast.success('Location deleted successfully');
        setDeleteDialog(false);
        fetchLocations();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete location');
    }
  };

  const getHealthColor = (score) => {
    if (score >= 75) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getHealthBg = (score) => {
    if (score >= 75) return 'bg-green-50 border-green-200';
    if (score >= 50) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Loading locations...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-1 sm:mb-2" style={{ fontFamily: 'Manrope' }}>Location Management</h1>
        <p className="text-sm sm:text-lg text-gray-600">Manage all locations, view health scores, and monitor activity</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Locations</p>
                <p className="text-2xl font-bold">{locations.length}</p>
              </div>
              <Building2 className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Locations</p>
                <p className="text-2xl font-bold">
                  {locations.filter(l => l.status !== 'inactive').length}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Users</p>
                <p className="text-2xl font-bold">
                  {locations.reduce((sum, l) => sum + (l.stats?.total_users || 0), 0)}
                </p>
              </div>
              <Users className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Leads</p>
                <p className="text-2xl font-bold">
                  {locations.reduce((sum, l) => sum + (l.stats?.total_leads || 0), 0)}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-cyan-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {locations.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-gray-600 text-center">No locations available</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {locations.map((location) => {
          const isCurrentLocation = location.id === currentLocation;
          const isInactive = location.status === 'inactive';
          
          return (
            <Card 
              key={location.id}
              className={`transition-all ${isInactive ? 'opacity-60' : ''} ${
                isCurrentLocation ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                      isCurrentLocation ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      {isInactive ? (
                        <PowerOff className="w-6 h-6 text-gray-600" />
                      ) : isCurrentLocation ? (
                        <CheckCircle className="w-6 h-6 text-blue-600" />
                      ) : (
                        <Building2 className="w-6 h-6 text-gray-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">{location.name}</CardTitle>
                        {isCurrentLocation && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                            Current
                          </span>
                        )}
                        {isInactive && (
                          <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full font-medium">
                            Inactive
                          </span>
                        )}
                      </div>
                      <CardDescription className="mt-1">
                        {location.city}, {location.state}
                      </CardDescription>
                    </div>
                  </div>
                  
                  {/* Health Score */}
                  <div className={`px-3 py-2 rounded-lg border ${getHealthBg(location.health_score)}`}>
                    <div className="text-xs text-gray-600 text-center">Health</div>
                    <div className={`text-xl font-bold text-center ${getHealthColor(location.health_score)}`}>
                      {location.health_score}
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                {/* Location Info */}
                {location.address && (
                  <div className="flex items-start gap-2 text-sm text-gray-600 mb-3">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{location.address}</span>
                  </div>
                )}
                
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                      <Users className="w-3 h-3" />
                      Users
                    </div>
                    <div className="text-sm font-semibold">
                      {location.stats?.active_users || 0} / {location.stats?.total_users || 0}
                    </div>
                    <div className="text-xs text-gray-500">active</div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                      <TrendingUp className="w-3 h-3" />
                      Leads
                    </div>
                    <div className="text-sm font-semibold">
                      {location.stats?.total_leads || 0}
                    </div>
                    <div className="text-xs text-gray-500">
                      {location.stats?.conversion_rate || 0}% converted
                    </div>
                  </div>
                </div>
                
                {/* Integration Status */}
                <div className="mb-4">
                  <div className="text-xs font-medium text-gray-600 mb-2">Integrations</div>
                  <div className="flex gap-2">
                    <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                      location.integrations?.gmail?.connected && location.integrations?.gmail?.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      <Mail className="w-3 h-3" />
                      Gmail
                    </div>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                      location.integrations?.gingr?.connected && location.integrations?.gingr?.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      <Database className="w-3 h-3" />
                      Gingr POS
                    </div>
                  </div>
                </div>
                
                {/* Last Activity */}
                {location.last_activity && (
                  <div className="text-xs text-gray-500 mb-4">
                    Last activity: {new Date(location.last_activity).toLocaleDateString()}
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 pt-3 border-t">
                  {!isCurrentLocation && !isInactive && (
                    <Button
                      onClick={() => handleSelectLocation(location.id)}
                      size="sm"
                      variant="outline"
                      className="flex-1"
                    >
                      Switch To
                    </Button>
                  )}
                  <Button
                    onClick={() => handleEditLocation(location)}
                    size="sm"
                    variant="outline"
                  >
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button
                    onClick={() => handleToggleStatus(location.id)}
                    size="sm"
                    variant="outline"
                    className={isInactive ? 'text-green-600' : 'text-gray-600'}
                  >
                    {isInactive ? <Power className="w-3 h-3" /> : <PowerOff className="w-3 h-3" />}
                  </Button>
                  <Button
                    onClick={() => handleDeleteClick(location)}
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit Location Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Location</DialogTitle>
            <DialogDescription>
              Update location details and contact information
            </DialogDescription>
          </DialogHeader>
          
          {editingLocation && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Location Name</Label>
                  <Input
                    id="name"
                    value={editingLocation.name || ''}
                    onChange={(e) => setEditingLocation({...editingLocation, name: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={editingLocation.phone || ''}
                    onChange={(e) => setEditingLocation({...editingLocation, phone: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={editingLocation.address || ''}
                  onChange={(e) => setEditingLocation({...editingLocation, address: e.target.value})}
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={editingLocation.city || ''}
                    onChange={(e) => setEditingLocation({...editingLocation, city: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={editingLocation.state || ''}
                    onChange={(e) => setEditingLocation({...editingLocation, state: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="zip">ZIP</Label>
                  <Input
                    id="zip"
                    value={editingLocation.zip || ''}
                    onChange={(e) => setEditingLocation({...editingLocation, zip: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact_name">Primary Contact Name</Label>
                  <Input
                    id="contact_name"
                    value={editingLocation.primary_contact_name || ''}
                    onChange={(e) => setEditingLocation({...editingLocation, primary_contact_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="contact_email">Primary Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={editingLocation.primary_contact_email || ''}
                    onChange={(e) => setEditingLocation({...editingLocation, primary_contact_email: e.target.value})}
                  />
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Location Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Delete Location
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {deletingLocation?.name}?
            </DialogDescription>
          </DialogHeader>
          
          {deleteStats && (
            <div className="py-4">
              {deleteStats.can_delete ? (
                <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <Info className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium mb-1">Safe to delete</p>
                    <p>This location has no users or leads. It can be safely deleted.</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-800">
                      <p className="font-medium mb-2">Cannot delete - location has data</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>{deleteStats.stats?.total_users || 0} users</li>
                        <li>{deleteStats.stats?.total_leads || 0} leads</li>
                      </ul>
                      <p className="mt-3">
                        Please reassign or delete all users and leads before deleting this location.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>
              Cancel
            </Button>
            {deleteStats?.can_delete && (
              <Button 
                onClick={handleConfirmDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete Location
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocationManagement;